<template>
  <div class="cmg-simulator cmg-simulator-ged">
    <template v-if="origin === 'contract_editor'">
      <h4>Paramètres du calcul d'aides</h4>
      <div class="segment">
        <AccordionGroup class="accordion--breakable" :exclusive="true">
          <AccordionItem title="Indiquer les informations familiales" :start-icon="iconUsers">
            <EmployerSettings v-bind="employerProps" v-on="employerEvents" />
          </AccordionItem>
          <AccordionItem title="Modifier les paramètres de la simulation" :start-icon="iconSettings">
            <div class="alert alert-warning alert--small">
              <p>Les paramètres ci-dessous ont été automatiquement remplis avec les informations du contrat.
                Attention, <b>ne les modifiez que pour expérimenter une autre configuration !</b></p>
            </div>
            <ContractSettingsGed v-bind="contractProps" v-on="contractEvents" />
          </AccordionItem>
        </AccordionGroup>
      </div>
    </template>
    <template v-else>
      <div class="row mb-5">
        <div class="col-12 col-md-4">
          <h3>Simulation</h3>
        </div>

        <div v-if="origin !== 'contract_editor'" class="col-12 col-md-8 d-flex justify-content-end flex-wrap gap-2">
          <div v-if="dossiers.length > 0">
            <button
              id="contract-select-dropdown"
              type="button"
              class="btn btn--secondary dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              🔍 {{ loadContractLabel }}
              <div class="dropdown-menu" aria-labelledby="contract-select-dropdown">
                <a
                  v-for="dossier in dossiers"
                  :key="dossier.contract_id"
                  class="dropdown-item"
                  href="#"
                  @click.prevent="loadContractFromApi(dossier.contract_id)"
                >
                  {{ dossier.name }}
                  <i v-if="dossier.childminder_short_name" class="font--xs">({{ dossier.childminder_short_name }})</i>
                </a>
              </div>
            </button>
          </div>

          <div v-else-if="!isAuthenticated">
            <Modal skin="bootstrap" title="Information">
              <template #trigger>
                <a href="javascript:;" class="btn btn--secondary">🔍 Charger les infos d'un contrat</a>
              </template>
              <div v-html="loginModalBody" />
            </Modal>
          </div>

          <button type="button" class="btn btn--secondary btn--left-icon no-print" @click="printSimulation">
            <span v-html="iconPrint" /> Imprimer
          </button>
        </div>
      </div>
    </template>

    <template v-if="origin !== 'contract_editor'">
      <h4>Informations employeur</h4>
      <div class="segment">
        <EmployerSettings v-bind="employerProps" v-on="employerEvents" />
      </div>
      <h4>Horaires de garde</h4>
      <div class="segment">
        <ContractSettingsGed v-bind="contractProps" v-on="contractEvents" />
      </div>
    </template>

    <div v-if="!sim.isCalculable.value && sim.monthWeek.value !== 'other'" class="help-notice">
      <div class="bubble">
        <p>Remplissez tous les champs de la section paramètres pour mettre à jour les résultats !</p>
      </div>
    </div>

    <div v-if="sim.monthWeek.value === 'other'" class="help-notice">
      <div class="bubble">
        <p>Vous avez un contrat un peu plus compliqué ? (périscolaire, multi-planning, etc.)</p>
        <p><b>Utilisez notre éditeur de contrat complet !</b></p>
        <p>Il calcule la mensualisation <b>pour tous les types de contrats</b> et calcule également
          <b>le montant de toutes les aides !</b> 🙂</p>
        <p class="text-center">
          <a :href="isAuthenticated ? '/home' : '/'" class="btn btn--primary" target="_blank">
            Essayer l'éditeur de contrat complet
          </a>
        </p>
      </div>
    </div>

    <SimulatorResults
      v-if="sim.isCalculable.value"
      care-type="ged"
      :is-calculable="sim.isCalculable.value"
      :is-public="sim.isPublic.value"
      :origin="origin"
      :has-overtime="sim.hasOvertime.value || sim.hasOvertimeFifty.value"
      :to-money="sim.toMoney"
      :gross-mensualization="sim.grossMensualization.value"
      :net-mensualization="sim.netMensualization.value"
      :gross-monthly-overtime-wage-premium="sim.grossMonthlyOvertimeWagePremium.value"
      :net-monthly-overtime-increase="sim.netMonthlyOvertimeIncrease.value"
      :net-paid-total="sim.netPaidTotal.value"
      :social-contributions-employee="sim.socialContributionsEmployee.value"
      :social-contributions-employer="sim.socialContributionsEmployer.value"
      :employer-contributions-reduction="sim.employerContributionsReduction.value"
      :total-amount="sim.totalAmount.value"
      :gross-total="sim.grossTotal.value"
      :social-contributions-credit="sim.socialContributionsCredit.value"
      :average-monthly-salaries="sim.averageMonthlySalaries.value"
      :income-reference-year="incomeReferenceYear"
      :cmg-eligible-amount="sim.cmgEligibleAmount"
      :amount-left-payable="sim.amountLeftPayable"
      :amount-left-payable-before-taxes="sim.amountLeftPayableBeforeTaxes"
      :taxes-credit="sim.taxesCredit"
      :daily-cost="sim.dailyCost"
      :hourly-cost="sim.hourlyCost"
      :cmg-not-available="sim.cmgNotAvailable.value"
      :child-too-old="sim.childTooOld.value"
      :cmg-aid-toggle="sim.cmgAidToggle.value"
      :other-aid-toggle="sim.otherAidToggle.value"
      :other-aid-amount="sim.otherAidF.value"
      :handicap-toggle="sim.handicapToggle.value"
      :handicap-toggle-infos="sim.handicapToggleInfos.value"
      :situation-french="sim.situationFrench.value"
      :children-count="sim.children.value"
      :child-age-french="sim.childAgeFrench.value"
      :child-age="sim.childAge.value"
      :external-sources="externalSources"
      :is-authenticated="isAuthenticated"
      :brand-name="brandName"
      :is-full-year="sim.isFullYear.value"
      :net-vacations-monthly="sim.netVacationsMonthly.value"
      :gross-vacations-monthly="sim.grossVacationsMonthly.value"
    />

    <PrintSection
      :brand-file-name="brandFileName"
      :brand-name="brandName"
      :brand-website="brandWebsite"
      :brand-host="brandHost"
      :situation-french="sim.situationFrench.value"
      :children="sim.children.value"
      :child-age-french="sim.childAgeFrench.value"
      :region-french="sim.regionFrench.value"
      :income-reference-year="incomeReferenceYear"
      :average-monthly-salary-parsed="sim.averageMonthlySalaryParsed.value"
      :other-aid-toggle="sim.otherAidToggle.value"
      :other-aid="sim.otherAid.value"
      :yearly-weeks="sim.yearlyWeeks.value"
      :weekly-worked-days="sim.weeklyWorkedDays.value"
      :weekly-hours="sim.weeklyHours.value"
      :monthly-worked-days="sim.monthlyWorkedDays.value"
      :monthly-hours="sim.monthlyHours.value"
      :monthly-overtime="sim.monthlyOvertime.value"
      :increase-overtime="sim.increaseOvertime.value"
      :has-overtime="sim.hasOvertime.value"
      :is-week="sim.isWeek.value"
      :is-calculable="sim.isCalculable.value"
      :weekly-overtime-float="sim.weeklyOvertimeFloat.value"
      :gross-hourly-wage-float="sim.grossHourlyWageF.value"
      :net-hourly-wage="sim.netHourlyWage.value"
      :to-money="sim.toMoney"
      :to-decimal4="sim.toDecimal4"
    >
      <template #cost-details>
        <div class="monthly_detail compact-print">
          <table class="table table-bordered font--xs">
            <tbody>
              <tr>
                <td>Mensualisation</td>
                <td>{{ sim.toMoney(sim.grossMensualization.value) }} brut <i>(environ {{ sim.toMoney(sim.netMensualization.value) }} net)</i></td>
              </tr>
              <tr v-if="sim.hasOvertime.value || sim.hasOvertimeFifty.value">
                <td>Estimation de la majoration heures majorées</td>
                <td>{{ sim.toMoney(sim.grossMonthlyOvertimeWagePremium.value) }} brut <i>(environ {{ sim.toMoney(sim.netMonthlyOvertimeIncrease.value) }} net)</i></td>
              </tr>
              <tr>
                <td>Montant moyen payé au salarié</td>
                <td>Environ {{ sim.toMoney(sim.netPaidTotal.value) }} / mois</td>
              </tr>
              <tr>
                <td>Estimation cotisations salariales</td>
                <td>Environ {{ sim.toMoney(sim.socialContributionsEmployee.value) }} <i>(incluses dans le brut)</i></td>
              </tr>
              <tr>
                <td>Estimation cotisations patronales</td>
                <td>Environ {{ sim.toMoney(sim.socialContributionsEmployer.value) }}</td>
              </tr>
              <tr>
                <td><strong>TOTAL AVANT AIDES</strong></td>
                <td><strong>{{ sim.toMoney(sim.totalAmount.value) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #aids-details>
        <div class="table-responsive">
          <table class="table table-striped table-bordered font--xs text-end align-middle">
            <thead>
              <tr>
                <th class="text-start">Salaire mensuel (net imposable) de l'employeur en {{ incomeReferenceYear }}</th>
                <th v-for="salary in sim.averageMonthlySalaries.value" :key="salary">{{ sim.toMoney(salary) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-start">Total mensuel</td>
                <td v-for="_ in sim.averageMonthlySalaries.value.length" :key="_">{{ sim.toMoney(sim.totalAmount.value) }}</td>
              </tr>
              <tr>
                <td class="text-start">Prise en charge partielle des cotisations sociales (CAF/Pajemploi)</td>
                <td v-for="_ in sim.averageMonthlySalaries.value.length" :key="_">- {{ sim.toMoney(sim.socialContributionsCredit.value) }}</td>
              </tr>
              <tr>
                <td class="text-start">Abattement de charges patronales</td>
                <td v-for="_ in sim.averageMonthlySalaries.value.length" :key="_">- {{ sim.toMoney(sim.employerContributionsReduction.value) }}</td>
              </tr>
              <tr>
                <td class="text-start">Aide CMG attribuée</td>
                <td v-for="salary in sim.averageMonthlySalaries.value" :key="salary">- {{ sim.toMoney(sim.cmgEligibleAmount(salary)) }}</td>
              </tr>
              <tr v-if="sim.otherAidToggle.value">
                <td class="text-start">Autres aides éventuelles</td>
                <td v-for="_ in sim.averageMonthlySalaries.value.length" :key="_">- {{ sim.toMoney(sim.otherAidF.value) }}</td>
              </tr>
              <tr>
                <td class="text-start"><strong>Reste à charge mensuel (estimation)</strong></td>
                <td v-for="salary in sim.averageMonthlySalaries.value" :key="salary"><strong>{{ sim.toMoney(sim.amountLeftPayableBeforeTaxes(salary)) }}</strong></td>
              </tr>
              <tr>
                <td class="text-start">Crédit d'impôt</td>
                <td v-for="salary in sim.averageMonthlySalaries.value" :key="salary">- {{ sim.toMoney(sim.taxesCredit(salary) / 12) }} <br /><i class="font--xs-light">({{ sim.toMoney(sim.taxesCredit(salary)) }} annuel)</i></td>
              </tr>
              <tr>
                <td class="text-start"><strong>Coût mensuel réel</strong></td>
                <td v-for="salary in sim.averageMonthlySalaries.value" :key="salary"><strong>{{ sim.toMoney(sim.amountLeftPayable(salary)) }}</strong></td>
              </tr>
              <tr>
                <td class="text-start">Coût journalier réel</td>
                <td v-for="salary in sim.averageMonthlySalaries.value" :key="salary">{{ sim.toMoney(sim.dailyCost(salary)) }} / jour</td>
              </tr>
              <tr>
                <td class="text-start">Coût horaire réel</td>
                <td v-for="salary in sim.averageMonthlySalaries.value" :key="salary">{{ sim.toMoney(sim.hourlyCost(salary)) }} / heure</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </PrintSection>

    <div class="text-center">
      <button type="button" class="btn btn--secondary no-print" @click="printSimulation">
        Imprimer la simulation
      </button>
    </div>

    <div v-if="origin === 'contract_editor'" class="help-notice mt-5 mb-5">
      <div class="bubble">
        <p>
          <strong>Notez bien : </strong>
          Essayez de modifier les paramètres de rémunération pour analyser l'impact sur le revenu du
          salarié et le coût final pour l'employeur&nbsp;!
        </p>
      </div>
    </div>

    <Modal v-if="sim.isPublic.value" v-model:open="memberOnlyModalOpen" skin="bootstrap" title="Information">
      <div class="text-center">
        <p>Ces résultats sont réservés à nos membres !</p>
        <p><a href="/signup_flow" class="btn btn--primary">Créer un compte</a></p>
        <p><a href="/accounts/sign_in" class="btn btn--secondary">Me connecter</a></p>
      </div>
    </Modal>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, watch, nextTick, ref } from 'vue';
import { AccordionGroup, AccordionItem, Modal } from '@twg/ui';
import { iconPrint, iconUsers, iconSettings } from '../icons';
import { useCmgSimulatorGed } from '../composables/useCmgSimulatorGed';
import { useCmgApi } from '../composables/useCmgApi';
import EmployerSettings from './EmployerSettings.vue';
import ContractSettingsGed from './ContractSettingsGed.vue';
import SimulatorResults from './SimulatorResults.vue';
import PrintSection from './PrintSection.vue';

export default defineComponent({
  name: 'CmgSimulatorGed',
  components: {
    EmployerSettings,
    ContractSettingsGed,
    SimulatorResults,
    PrintSection,
    AccordionGroup,
    AccordionItem,
    Modal,
  },
  props: {
    origin: { type: String, default: 'single_tool' },
    contractId: { type: [String, Number], default: null },
  },
  setup(props) {
    const sim = useCmgSimulatorGed();
    const api = useCmgApi();
    const CARE_TYPE = 'ged';

    const isAuthenticated = ref(false);
    const regions = ref([]);
    const dossiers = ref([]);
    const externalSources = ref({});
    const incomeReferenceYear = ref(new Date().getFullYear() - 2);
    const brandName = ref('');
    const brandWebsite = ref('');
    const brandHost = ref('');
    const brandFileName = ref('');
    const loadContractLabel = ref("Charger les infos d'un contrat");

    onMounted(async() => {
      const [, pageConfigData] = await Promise.all([
        api.fetchSettings(CARE_TYPE),
        api.fetchPageConfig(props.origin, props.contractId, CARE_TYPE),
      ]);

      if (api.settings.value) {
        sim.initialize(api.settings.value);
      }

      if (pageConfigData) {
        isAuthenticated.value = pageConfigData.is_authenticated;
        regions.value = pageConfigData.regions.map(r => ({ label: r.label, value: r.value }));
        dossiers.value = pageConfigData.dossiers || [];
        externalSources.value = pageConfigData.external_sources || {};
        incomeReferenceYear.value = pageConfigData.income_reference_year;
        brandName.value = pageConfigData.brand_name || '';
        brandWebsite.value = pageConfigData.brand_website || '';
        brandHost.value = pageConfigData.brand_host || '';
        brandFileName.value = pageConfigData.brand_file_name || '';

        if (!pageConfigData.is_authenticated) {
          sim.isPublic.value = true;
        }
      }

      await nextTick();
      sim.toggleMW();
      sim.monthFromWeek();

      const autoLoadContractId = props.contractId ||
        (dossiers.value.length === 1 ? dossiers.value[0].contract_id : null);

      if (autoLoadContractId) {
        const contractData = await api.fetchContractData(autoLoadContractId);
        if (contractData) {
          sim.loadFromContract(contractData);
          if (contractData.income_reference_year != null) {
            incomeReferenceYear.value = contractData.income_reference_year;
          }
        }
      }
    });

    watch(() => sim.weeklyWorkedDays.value, () => {
      sim.monthFromWeek();
      sim.checkErrors();
    });

    watch(() => sim.weeklyHours.value, () => {
      sim.monthFromWeek();
      sim.hasOvertime.value = sim.weeklyHoursF.value > sim.maxNormalWorkingHours;
      sim.hasOvertimeFifty.value = sim.weeklyHoursF.value > sim.maxOvertimeForty;
      sim.checkErrors();
    });

    watch(() => sim.hasOvertime.value, () => {
      setTimeout(() => sim.checkErrors());
    });

    watch(() => sim.monthWeek.value, () => {
      sim.toggleMW();
    });

    watch(() => sim.yearlyWeeks.value, () => {
      if (sim.monthWeek.value === 'week') sim.monthFromWeek();
      sim.yearlyWeeks.value = isNaN(sim.yearlyWeeksF.value)
        ? ''
        : sim.toDecimal4(sim.yearlyWeeksF.value).toString().replace('.', ',');
      sim.checkErrors();
    });

    watch(() => sim.monthlyWorkedDays.value, () => sim.checkErrors());
    watch(() => sim.monthlyHours.value, () => sim.checkErrors());
    watch(() => sim.monthlyOvertime.value, () => sim.checkErrors());
    watch(() => sim.monthlyOvertimeFifty.value, () => sim.checkErrors());

    const memberOnlyModalOpen = ref(false);
    let blurClickHandlerAttached = false;
    watch(() => sim.isPublic.value, (val) => {
      if (val && !blurClickHandlerAttached) {
        blurClickHandlerAttached = true;
        document.body.addEventListener('click', (e) => {
          if (e.target.closest('.blurring')) {
            memberOnlyModalOpen.value = true;
          }
        });
      }
    });

    watch(() => sim.grossHourlyWageInput.value, (val) => {
      const floatValue = sim.shownStringToFloat(val);
      sim.isHourlyRateInRange.value = !(
        floatValue > sim.maximumGrossHourlyWage.value ||
        floatValue < sim.minimumGrossHourlyWage.value
      );
    });

    function loadContractFromApi(contractId) {
      if (!contractId) {
        window.location.reload();
        return;
      }
      api.fetchContractData(contractId).then(data => {
        if (data) {
          sim.loadFromContract(data);
          loadContractLabel.value = data.load_contract_label || loadContractLabel.value;
          if (data.income_reference_year != null) {
            incomeReferenceYear.value = data.income_reference_year;
          }
        }
      });
    }

    function printSimulation() {
      sim.print();
    }

    const loginModalBody = '' +
      '<p>En vous connectant à votre compte, vous pouvez directement remplir les données de ce simulateur avec les informations de l\'un de vos contrats. 🙂</p>' +
      '<p class="text-center"><a href="/accounts/sign_in" class="btn btn--secondary">Me connecter</a></p>' +
      '<div class="help-notice">' +
      '<div class="bubble"><p>Vous n\'avez pas encore de compte ? Inscrivez-vous, c\'est gratuit !</p><p class="text-center"><a href="/signup_flow" class="btn btn--primary">Créer un compte</a></p></div></div>';

    const employerProps = computed(() => ({
      careType: 'ged',
      situation: sim.situation.value,
      children: sim.children.value,
      childAge: sim.childAge.value,
      region: sim.region.value,
      isCustomMonthlySalary: sim.isCustomMonthlySalary.value,
      averageMonthlySalary: sim.averageMonthlySalary.value,
      cmgAidToggle: sim.cmgAidToggle.value,
      otherAidToggle: sim.otherAidToggle.value,
      otherAid: sim.otherAid.value,
      handicapToggle: sim.handicapToggle.value,
      sharedCostToggle: sim.sharedCostToggle.value,
      sharedCost: sim.sharedCost.value,
      firstEmployeeDeclaration: sim.firstEmployeeDeclaration.value,
      origin: props.origin,
      regions: regions.value,
      externalSources: externalSources.value,
      incomeReferenceYear: incomeReferenceYear.value,
    }));

    const employerEvents = {
      'update:situation': (v) => { sim.situation.value = v; },
      'update:children': (v) => { sim.children.value = v; },
      'update:childAge': (v) => { sim.childAge.value = v; },
      'update:region': (v) => { sim.region.value = v; },
      'update:isCustomMonthlySalary': (v) => { sim.isCustomMonthlySalary.value = v; },
      'update:averageMonthlySalary': (v) => { sim.averageMonthlySalary.value = v; },
      'update:cmgAidToggle': (v) => { sim.cmgAidToggle.value = v; },
      'update:otherAidToggle': (v) => { sim.otherAidToggle.value = v; },
      'update:otherAid': (v) => { sim.otherAid.value = v; },
      'update:handicapToggle': (v) => { sim.handicapToggle.value = v; },
      'update:sharedCostToggle': (v) => { sim.sharedCostToggle.value = v; },
      'update:sharedCost': (v) => { sim.sharedCost.value = v; },
      'update:firstEmployeeDeclaration': (v) => { sim.firstEmployeeDeclaration.value = v; },
    };

    const contractProps = computed(() => ({
      monthWeek: sim.monthWeek.value,
      weeklyWorkedDays: sim.weeklyWorkedDays.value,
      weeklyHours: sim.weeklyHours.value,
      yearlyWeeks: sim.yearlyWeeks.value,
      monthlyWorkedDays: sim.monthlyWorkedDays.value,
      monthlyHours: sim.monthlyHours.value,
      hasOvertime: sim.hasOvertime.value,
      monthlyOvertime: sim.monthlyOvertime.value,
      monthlyOvertimeFifty: sim.monthlyOvertimeFifty.value,
      increaseOvertime: sim.increaseOvertime.value,
      increaseOvertimeFifty: sim.increaseOvertimeFifty.value,
      maxNormalWorkingHours: sim.maxNormalWorkingHours,
      maxOvertimeForty: sim.maxOvertimeForty,
      maxOvertimeFifty: sim.maxOvertimeFifty,
      contractEndsBeforeOneYear: sim.contractEndsBeforeOneYear.value,
      schedulableWeeks: sim.schedulableWeeks.value,
      weeksWorked: sim.weeksWorked.value,
      grossHourlyWageInput: sim.grossHourlyWageInput.value,
      grossHourlyWageFloat: sim.grossHourlyWageF.value,
      netRatioClassic: sim.netRatioClassic.value,
      isHourlyRateInRange: sim.isHourlyRateInRange.value,
      minimumGrossHourlyWage: sim.minimumGrossHourlyWage.value,
      maximumGrossHourlyWage: sim.maximumGrossHourlyWage.value,
      maxCMGHelpExceeded: sim.maxCMGHelpExceeded.value,
      maxNetHelp: sim.maxNetHelp.value,
      isMonthlyWorkedDaysValid: sim.isMonthlyWorkedDays.value,
      isMonthlyHoursValid: sim.isMonthlyHours.value,
      isWeeklyWorkedDaysValid: sim.isWeeklyWorkedDays.value,
      isWeeklyHoursValid: sim.isWeeklyHours.value,
      isYearlyWeeksValid: sim.isYearlyWeeks.value,
      isMonthlyOvertimeValid: sim.isMonthlyOvertime.value,
      origin: props.origin,
      toFloatForDisplay: sim.toFloatForDisplay,
      toMoney: sim.toMoney,
      toDecimal4: sim.toDecimal4,
    }));

    const contractEvents = {
      'update:monthWeek': (v) => { sim.monthWeek.value = v; },
      'update:weeklyWorkedDays': (v) => { sim.weeklyWorkedDays.value = v; },
      'update:weeklyHours': (v) => { sim.weeklyHours.value = v; },
      'update:yearlyWeeks': (v) => { sim.yearlyWeeks.value = v; },
      'update:monthlyWorkedDays': (v) => { sim.monthlyWorkedDays.value = v; },
      'update:monthlyHours': (v) => { sim.monthlyHours.value = v; },
      'update:hasOvertime': (v) => { sim.hasOvertime.value = v; },
      'update:monthlyOvertime': (v) => { sim.monthlyOvertime.value = v; },
      'update:monthlyOvertimeFifty': (v) => { sim.monthlyOvertimeFifty.value = v; },
      'update:increaseOvertime': (v) => { sim.increaseOvertime.value = v; },
      'update:increaseOvertimeFifty': (v) => { sim.increaseOvertimeFifty.value = v; },
      'update:grossHourlyWageInput': (v) => { sim.grossHourlyWageInput.value = v; },
      adjustHourlyRate: (dir) => { sim.adjustHourlyRate(dir); },
      updateHourlyWage: () => { sim.updateHourlyWage(); },
      'slider-ready': (instance) => { sim.slider.value = instance; },
      setGrossHourlyWageFromSlider: (v) => { sim.setGrossHourlyWageFromSlider(v); },
    };

    return {
      sim,
      isAuthenticated,
      dossiers,
      externalSources,
      incomeReferenceYear,
      brandName,
      brandWebsite,
      brandHost,
      brandFileName,
      loadContractLabel,
      employerProps,
      employerEvents,
      contractProps,
      contractEvents,
      loadContractFromApi,
      printSimulation,
      loginModalBody,
      memberOnlyModalOpen,
      iconPrint,
      iconUsers,
      iconSettings,
    };
  },
});
</script>
